<template>
    <div class="bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />
        <h2 class="text-xl font-semibold mb-4 text-gray-800">Update Invoice SPK Subcon</h2>
        <form @submit.prevent="update">
            <div class="grid grid-cols-1 gap-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="invoiceNo">Pilih Invoice
                            #</label>
                        <select
                            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                            id="invoiceNo" v-model="invoiceId">
                            <option value="">-- Pilih Invoice --</option>
                            <option v-for="invoice in allInvoices" :key="invoice.id" :value="invoice.id">
                                {{ invoice.invnum }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="invoiceDate">Invoice Date</label>
                        <input class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                            id="invoiceDate" type="date" v-model="invoiceDate" />
                    </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="invoiceSubconNo">Invoice Subcon
                            #</label>
                        <input class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                            id="invoiceSubconNo" type="text" v-model="invoiceSubconNo"
                            placeholder="Nomor Invoice dari Subcon" />
                    </div>
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="receivedDate">Received
                            Date</label>
                        <input class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                            id="receivedDate" type="date" v-model="receivedDate" />
                    </div>
                </div>

                <div>
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="subcon">Subcon</label>
                        <select
                            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                            :class="{ 'bg-gray-100': invoiceId }" id="subcon" v-model="subconId"
                            :disabled="!!invoiceId">
                            <option value="">-- Pilih Subcon --</option>
                            <option v-for="subcon in uniqueSubcons" :key="subcon.id" :value="subcon.id">
                                {{ subcon.companyName }}
                            </option>
                        </select>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                            <label class="block text-gray-700 text-sm font-bold mb-2">Rek. Name</label>
                            <input type="text" :value="selectedSubconInfo?.rekName || ''"
                                class="input-border bg-gray-100 shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                                readonly />
                        </div>
                        <div>
                            <label class="block text-gray-700 text-sm font-bold mb-2">Bank</label>
                            <input type="text" :value="selectedSubconInfo?.bankName || ''"
                                class="input-border bg-gray-100 shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                                readonly />
                        </div>
                        <div>
                            <label class="block text-gray-700 text-sm font-bold mb-2">Rekening</label>
                            <input type="text" :value="selectedSubconInfo?.rekening || ''"
                                class="input-border bg-gray-100 shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"
                                readonly />
                        </div>
                    </div>
                </div>

                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="spk">SPK #</label>
                    <div class="flex items-center">
                        <select
                            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            id="spk" v-model="selectedSpkFileId">
                            <option value="">-- Pilih SPK --</option>
                            <option v-for="spkFile in availableSpkFiles" :key="spkFile.Id" :value="spkFile.Id">
                                {{ spkFile.SpkNum }}
                            </option>
                        </select>
                        <button @click="addSpkToList" type="button"
                            class="ml-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm focus:outline-none focus:shadow-outline whitespace-nowrap">
                            Add to List
                        </button>
                    </div>
                </div>

                <div class="overflow-x-auto mb-4">
                    <table class="min-w-full table-auto">
                        <thead class="bg-gray-200">
                            <tr>
                                <th class="px-4 py-2 border-b text-left">Chk.</th>
                                <th class="px-4 py-2 border-b text-left">No</th>
                                <th class="hidden">SPK Detail ID</th>
                                <th class="hidden">SPK ID</th>
                                <th class="px-4 py-2 border-b text-left">Site Name</th>
                                <th class="px-4 py-2 border-b text-left">Area</th>
                                <th class="px-4 py-2 border-b text-left">Project</th>
                                <th class="px-4 py-2 border-b text-left">Price</th>
                                <th class="px-4 py-2 border-b text-left">Balance Invoice</th>
                                <th class="px-4 py-2 border-b text-left">Start Date</th>
                                <th class="px-4 py-2 border-b text-left">Job Type</th>
                                <th class="px-4 py-2 border-b text-left">SPK #</th>
                                <th class="px-4 py-2 border-b text-left">Amount Invoice</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="invoiceDetails.length === 0">
                                <td colspan="13" class="text-center py-4 border-b">No SPK added yet.</td>
                            </tr>
                            <tr v-for="(item, index) in invoiceDetails" :key="item.detailId">
                                <td class="px-4 py-2 border-b"><input type="checkbox" v-model="item.selected"
                                        :disabled="item.balance === 0 || item.isAlreadyInvoiced" /></td>
                                <td class="px-4 py-2 border-b">{{ index + 1 }}</td>
                                <td class="hidden">{{ item.detailId }}</td>
                                <td class="hidden">{{ item.spkFileId }}</td>
                                <td class="px-4 py-2 border-b">{{ item.siteName }}</td>
                                <td class="px-4 py-2 border-b">{{ item.area }}</td>
                                <td class="px-4 py-2 border-b">{{ item.project }}</td>
                                <td class="px-4 py-2 border-b">{{ formatCurrency(item.price) }}</td>
                                <td class="px-4 py-2 border-b">{{ formatCurrency(item.balance) }}</td>
                                <td class="px-4 py-2 border-b">{{ formatDate(item.startDate) }}</td>
                                <td class="px-4 py-2 border-b">{{ item.jobType }}</td>
                                <td class="px-4 py-2 border-b">{{ item.spkNo }}</td>
                                <td class="px-4 py-2 border-b">
                                    <input type="number" v-model.number="item.invoiceAmount"
                                        class="w-32 px-2 py-1 text-sm border rounded" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="flex justify-end mt-4">
                    <span class="text-lg font-semibold text-gray-700">Total:</span>
                    <span class="text-lg font-bold text-gray-900 ml-4">{{ formatCurrency(totalSelectedInvoiceAmount)
                    }}</span>
                </div>
                <div class="mt-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="remarks">Remarks</label>
                    <textarea id="remarks" v-model="remarks" rows="3"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"></textarea>
                </div>
            </div>

            <div class="flex justify-center mt-6">
                <button
                    class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline mr-2"
                    type="submit">
                    Update
                </button>
                <button
                    class="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline"
                    type="button" @click="cancel">
                    Cancel
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

export default {
    name: 'InvoiceUpdate',
    components: {
        MonkeyLoader
    },
    data() {
        return {
            allInvoices: [],
            invoiceId: '',
            invoiceNo: '',
            invoiceDate: new Date().toLocaleDateString('en-CA'),
            invoiceSubconNo: '',
            receivedDate: new Date().toLocaleDateString('en-CA'),
            subconId: '',
            selectedSpkFileId: '',
            invoiceDetails: [],
            combinedSubconData: [],
            currentSubconSpks: [],
            remarks: '',
        };
    },
    computed: {
        uniqueSubcons() {
            const seen = new Map();
            this.combinedSubconData.forEach(item => {
                if (!seen.has(item.entityId)) {
                    seen.set(item.entityId, { id: item.entityId, companyName: item.entityName });
                }
            });
            return Array.from(seen.values());
        },
        selectedSubconInfo() {
            if (!this.subconId) return null;
            const data = this.combinedSubconData.find(d => d.entityId === this.subconId);
            if (!data) return null;
            return { rekName: data.contactName, bankName: data.bankName, rekening: data.contactRek };
        },
        availableSpkFiles() {
            if (!this.currentSubconSpks) return [];
            return this.currentSubconSpks.filter(spkFile =>
                spkFile.TblSpksubconDetails && spkFile.TblSpksubconDetails.some(detail => detail.Status === 0)
            );
        },
        totalSelectedInvoiceAmount() {
            return this.invoiceDetails
                .filter(item => item.selected)
                .reduce((total, item) => total + parseFloat(item.invoiceAmount || 0), 0);
        },
    },
    methods: {
        formatDateFromAPI(dateString) {
            if (!dateString) return '';
            return dateString.slice(0, 10);
        },
        async fetchInvoiceData(id) {
            if (!id) {
                this.resetFormFields();
                return;
            }
            try {
                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };
                const response = await axios.get(`${window.location.origin}/api/invoicesubcon/${id}`, { headers });
                const invoiceData = response.data;

                this.invoiceId = invoiceData.id;
                this.invoiceNo = invoiceData.invnum;
                this.invoiceDate = this.formatDateFromAPI(invoiceData.invdate);
                this.invoiceSubconNo = invoiceData.invsubconnum;
                this.receivedDate = this.formatDateFromAPI(invoiceData.receiveddate);
                this.subconId = invoiceData.entityid;
                this.remarks = invoiceData.remark;

                const poDataCache = new Map();
                const detailPromises = invoiceData.invspksubcondetails.map(async (detail) => {
                    let projectName = '', areaName = '';
                    const spkSubconDetailId = detail.spksubcondetailid;

                    if (detail.poId) {
                        if (poDataCache.has(detail.poId)) {
                            ({ projectName, areaName } = poDataCache.get(detail.poId));
                        } else {
                            try {
                                const projectResponse = await axios.get(`https://meeting-backend.iotech.my.id/projects-bypo/${detail.poId}`, { headers });
                                if (projectResponse.data && projectResponse.data.length > 0) {
                                    const projectInfo = projectResponse.data[0];
                                    projectName = projectInfo.name;
                                    areaName = projectInfo.city ? projectInfo.city.name : '';
                                    poDataCache.set(detail.poId, { projectName, areaName });
                                }
                            } catch (error) { console.error(`Gagal mengambil data untuk PO ID ${detail.poId}:`, error); }
                        }
                    }

                    const balance = detail.balanceinv;
                    const isAlreadyInvoiced = balance === 0;

                    return {
                        selected: true,
                        detailId: spkSubconDetailId,
                        spkFileId: detail.spkFileId,
                        siteName: detail.sitename,
                        area: areaName,
                        project: projectName,
                        price: detail.originalPrice,
                        balance: balance,
                        invoiceAmount: detail.invoicePrice,
                        startDate: detail.startDate,
                        jobType: this.getJobTypeName(detail.spkJob),
                        spkNo: detail.spkNum,
                        isAlreadyInvoiced: isAlreadyInvoiced,
                    };
                });
                this.invoiceDetails = await Promise.all(detailPromises);
            } catch (error) {
                console.error("Gagal mengambil data invoice:", error);
                Swal.fire('Error', 'Gagal memuat data invoice untuk diupdate.', 'error');
            }
        },
        resetFormFields() {
            this.invoiceId = '';
            this.invoiceNo = '';
            this.invoiceDate = new Date().toLocaleDateString('en-CA');
            this.invoiceSubconNo = '';
            this.receivedDate = new Date().toLocaleDateString('en-CA');
            this.subconId = '';
            this.selectedSpkFileId = '';
            this.invoiceDetails = [];
            this.combinedSubconData = [];
            this.currentSubconSpks = [];
            this.remarks = '';
        },
        removeDetail(index) {
            this.invoiceDetails.splice(index, 1);
        },
        async update() {
            const selectedDetails = this.invoiceDetails.filter(d => d.selected);
            if (!this.invoiceId || !this.invoiceNo || !this.subconId || selectedDetails.length === 0) {
                Swal.fire('Warning', 'Harap lengkapi semua field dan pastikan ada minimal satu item SPK.', 'warning');
                return;
            }

            for (const detail of selectedDetails) {
                if (detail.invoiceAmount <= 0) {
                    Swal.fire('Warning', 'Jumlah invoice harus lebih dari 0.', 'warning');
                    return;
                }

            }
            const totalInvoice = this.totalSelectedInvoiceAmount;
            const noUrut = parseInt(this.invoiceNo.split('-')[2], 10) || 0;

            const payload = {
                InvNum: this.invoiceNo,
                InvDate: this.invoiceDate,
                InvSubconNum: this.invoiceSubconNo,
                ReceivedDate: this.receivedDate,
                InvTotal: totalInvoice,
                EntityId: this.subconId,
                NoUrut: noUrut,
                Status: 0,
                Remark: this.remarks,
                invspksubcondetails: selectedDetails.map(d => ({
                    SpkSubconDetailId: d.detailId,
                    Price: d.invoiceAmount,
                    Status: 0
                })),
            };

            try {
                const token = localStorage.getItem('token');
                await axios.put(`${window.location.origin}/api/invoicesubcon/${this.invoiceId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                await Swal.fire({
                    title: 'Success!',
                    text: 'Invoice berhasil diupdate!',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

                this.resetFormFields();
                this.fetchData();
            } catch (error) {
                console.error('Gagal mengupdate invoice:', error);
                Swal.fire('Error', `Gagal menyimpan perubahan. ${error.response?.data?.message || ''}`, 'error');
            }
        },
        async fetchData() {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            try {
                const [subconCombinedRes, allInvoicesRes] = await Promise.all([
                    axios.get(`${window.location.origin}/api/spksubcon/combined-data`, { headers }),
                    axios.get(`${window.location.origin}/api/invoicesubcon`, { headers }),
                ]);
                this.combinedSubconData = subconCombinedRes.data;
                this.allInvoices = allInvoicesRes.data;
            } catch (error) {
                console.error('Gagal mengambil data awal:', error);
                Swal.fire('Error', 'Gagal memuat data awal dari server.', 'error');
            }
        },
        cancel() {
            this.resetFormFields();
            this.fetchData();
        },
        getJobTypeName(jobId) {
            const jobTypes = { 1: 'Survey', 2: 'Install', 3: 'ATP', 4: 'Troubleshoot' };
            return jobTypes[jobId] || 'Unknown';
        },
        formatCurrency(value) {
            if (value === null || value === undefined) return '';
            return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(value);
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            const date = new Date(dateString.slice(0, 10)); // Memperbaiki pemformatan tanggal
            return date.toLocaleDateString('id-ID', options);
        },
        async addSpkToList() {
            if (!this.selectedSpkFileId) {
                Swal.fire('Info', 'Silakan pilih SPK terlebih dahulu.', 'info');
                return;
            }
            const spkFileToAdd = this.currentSubconSpks.find(f => f.Id === this.selectedSpkFileId);
            if (!spkFileToAdd || !spkFileToAdd.TblSpksubconDetails) return;

            const existingDetailIds = this.invoiceDetails.map(d => d.detailId);
            const detailsToProcess = spkFileToAdd.TblSpksubconDetails.filter(detail =>
                !existingDetailIds.includes(detail.Id) && detail.Status === 0
            );

            if (detailsToProcess.length === 0) {
                Swal.fire('Info', 'Semua detail yang tersedia dari SPK ini sudah ditambahkan atau sudah di-invoice sebelumnya.', 'info');
                return;
            }

            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const poDataCache = new Map();

            const newDetailsPromises = detailsToProcess.map(async (detail) => {
                let projectName = '', areaName = '';
                const poId = detail.PoId;

                if (poId) {
                    if (poDataCache.has(poId)) {
                        ({ projectName, areaName } = poDataCache.get(poId));
                    } else {
                        try {
                            const response = await axios.get(`https://meeting-backend.iotech.my.id/projects-bypo/${poId}`, { headers });
                            if (response.data && response.data.length > 0) {
                                const projectInfo = response.data[0];
                                projectName = projectInfo.name;
                                areaName = projectInfo.city ? projectInfo.city.name : '';
                                poDataCache.set(poId, { projectName, areaName });
                            }
                        } catch (error) { console.error(`Gagal mengambil data untuk PO ID ${poId}:`, error); }
                    }
                }

                const balance = detail.balanceinv;
                const isAlreadyInvoiced = balance === 0;

                return {
                    selected: !isAlreadyInvoiced,
                    detailId: detail.Id,
                    spkFileId: spkFileToAdd.Id,
                    siteName: detail.Sitename,
                    area: areaName,
                    project: projectName,
                    price: detail.Price,
                    balance: balance,
                    invoiceAmount: balance,
                    startDate: detail.StartDate,
                    jobType: this.getJobTypeName(spkFileToAdd.SpkJob),
                    spkNo: spkFileToAdd.SpkNum,
                    isAlreadyInvoiced: isAlreadyInvoiced
                };
            });
            this.invoiceDetails.push(...(await Promise.all(newDetailsPromises)));
            this.selectedSpkFileId = '';
        }
    },
    watch: {
        invoiceId(newId) {
            this.fetchInvoiceData(newId);
        },
        async subconId(newId) {
            if (!newId || this.invoiceId) return;
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${window.location.origin}/api/subconfilesinv/${newId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                this.currentSubconSpks = response.data;
            } catch (error) {
                console.error(`Gagal mengambil data SPK untuk subcon ${newId}:`, error);
                Swal.fire('Error', 'Gagal memuat data SPK untuk subcon ini.', 'error');
            }
        }
    },
    mounted() {
        this.fetchData();
    },
};
</script>

<style scoped>
.input-border {
    border-bottom: 2px solid #ccc;
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-top: none;
}

.input-border:focus {
    border-bottom-color: #007bff;
    box-shadow: none;
}
</style>